/**
 * @file MicPicker.tsx
 * @brief Boardroom microphone picker — PickerSheet (mobile mic picker feel).
 *
 * @author DES
 * @date 28-07-2026
 * @updated 29-07-2026 — compact variant for meeting-setup header
 */

import { useMemo, useState } from "react";
import { Bluetooth, ChevronsUpDown, Headphones, Mic, Usb } from "lucide-react";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { useFieldStyle } from "@/components/ui/fieldStyle";
import { cn } from "@/shared/theme/cn";
import type { MicDevice } from "./capture/useMicDevices";

const DEFAULT_ID = "";

type MicPickerItem = PickerSheetItem & {
  deviceId: string;
};

function describeMic(label: string): string {
  const l = label.toLowerCase();
  if (l.includes("bluetooth") || l.includes("airpod") || l.includes("headset")) {
    return "Bluetooth";
  }
  if (l.includes("usb") || l.includes("external")) return "USB / external";
  if (
    l.includes("built-in") ||
    l.includes("internal") ||
    l.includes("macbook") ||
    l.includes("iphone")
  ) {
    return "Built-in";
  }
  if (l.includes("wire") || l.includes("headset")) return "Wired";
  return "Microphone";
}

/**
 * One line naming the mic actually in use, for a field hint. With "System
 * default" it names the device the browser picks (Chrome lists it as
 * "Default - <name>"; Safari puts it first); with a chosen device it says
 * what kind it is, since the trigger already shows its name. Capped so the
 * hint never wraps and pushes the field's hairline down.
 */
export function describeMicInUse(devices: MicDevice[], value: string, loading?: boolean): string {
  if (loading) return "Detecting…";
  if (devices.length === 0) return "No microphone found";
  let text: string;
  if (value === DEFAULT_ID) {
    const fallback = devices.find((d) => d.deviceId === "default") ?? devices[0]!;
    text = `Using ${fallback.label.replace(/^default\s*-\s*/i, "")}`;
  } else {
    const chosen = devices.find((d) => d.deviceId === value);
    if (!chosen) return "Microphone not connected";
    const kind = describeMic(chosen.label);
    text = kind === "Microphone" ? "External mic" : `${kind} mic`;
  }
  return text.length > 42 ? `${text.slice(0, 41).trimEnd()}…` : text;
}

/**
 * `sm` = closed trigger; `field` = inside a bare form field (20px, as the
 * Gender picker's circle, so the field's hairline lines up with its
 * neighbours); `xl` = flush PickerSheet leading circle (56px).
 */
function MicIcon({ label, size = "sm" }: { label: string; size?: "sm" | "field" | "xl" }) {
  const kind = describeMic(label);
  const glyph =
    size === "xl"
      ? "h-6 w-6 text-muted-foreground"
      : size === "field"
        ? "h-3 w-3 text-muted-foreground"
        : "h-4 w-4 text-muted-foreground";
  let icon = <Mic className={glyph} />;
  if (kind === "Bluetooth") icon = <Bluetooth className={glyph} />;
  else if (kind === "USB / external") icon = <Usb className={glyph} />;
  else if (kind === "Wired") icon = <Headphones className={glyph} />;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground",
        size === "xl" ? "h-14 w-14" : size === "field" ? "h-5 w-5" : "h-7 w-7",
      )}
    >
      {icon}
    </span>
  );
}

export function MicPicker({
  devices,
  value,
  onChange,
  disabled,
  loading,
  error,
  compact = false,
}: {
  devices: MicDevice[];
  /** Empty string = system default */
  value: string;
  onChange: (deviceId: string) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
  /** Header chrome — short trigger, no helper copy. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { bare } = useFieldStyle();

  const items: MicPickerItem[] = useMemo(() => {
    const rows: MicPickerItem[] = [
      {
        id: DEFAULT_ID,
        deviceId: DEFAULT_ID,
        label: "System default",
        subtitle: "Let the browser choose",
      },
    ];
    for (const d of devices) {
      rows.push({
        id: d.deviceId,
        deviceId: d.deviceId,
        label: d.label,
        subtitle: describeMic(d.label),
        searchKeys: [d.label, describeMic(d.label)],
      });
    }
    return rows;
  }, [devices]);

  const selected = items.find((i) => i.id === value) ?? items[0]!;
  const tip = "Prefer a USB or Bluetooth conference mic for multi-person rooms.";

  return (
    <div className={cn(!compact && "space-y-2", compact && "shrink-0")}>
      {!compact && error ? <p className="text-sm text-destructive">{error}</p> : null}
      {compact ? null : <p className="text-xs text-muted-foreground">{tip}</p>}

      <PickerSheet<MicPickerItem>
        anchor={
          <button
            type="button"
            disabled={disabled || loading}
            aria-label={`Microphone: ${loading ? "Detecting…" : selected.label}`}
            title={tip}
            className={cn(
              // Same stadium pill as LanguagePicker / boardroom picker list triggers.
              "flex items-center justify-between gap-2 border border-input/90 bg-field text-sm font-medium text-foreground transition-colors",
              "rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              compact ? "h-11 w-auto max-w-[16rem] shrink-0 px-3" : "h-11 w-full px-3",
            )}
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <MicIcon label={selected.label} size={bare ? "field" : "sm"} />
              <span className="truncate">
                {loading ? (compact ? "Detecting…" : "Detecting microphones…") : selected.label}
              </span>
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        }
        open={open}
        onOpenChange={setOpen}
        title={devices.length > 0 ? `Choose microphone (${devices.length})` : "Choose microphone"}
        subtitle="Same idea as the mobile boardroom mic picker."
        searchPlaceholder="Search microphones…"
        items={items}
        selectedId={selected.id}
        onSelect={(item) => onChange(item.deviceId)}
        clearable={false}
        loading={loading}
        renderLeftAccessory={(item) => <MicIcon label={item.label} size="xl" />}
        emptyText="No microphones found. Check browser permission."
      />
    </div>
  );
}
